//
// # Pin Snap
//
// Magnetic snap to nearby pins when drawing wires.
// When the cursor is within a snap radius of a pin, the wire endpoint
// snaps to the exact pin location, ensuring proper connectivity.
//

import { Point } from "GenAlphaModel";
import { getSchPinWorldLocation, pointKey } from "./wire_utils";
import type { SchematicRender } from "../content/schematic/render";

// Snap radius in schematic units (pixels at 1:1 zoom)
// Pins within this distance from the cursor will be snapped to
export const PIN_SNAP_RADIUS = 15;

// Result of a pin snap check
export interface PinSnapResult {
  // The pin location to snap to, or null if no pin is nearby
  snapPoint: Point | null;
  // Distance to the nearest pin (for debugging/feedback)
  distance: number | null;
}

// Cache for pin locations to avoid recomputing every frame
export interface PinLocationCache {
  // All pin locations indexed by point key for O(1) lookup
  pinsByKey: Map<string, Point>;
  // All pin locations as array for distance search
  allPins: Point[];
}

// Build a cache of all pin locations in a schematic.
// The schematic must be valid and non-null.
export function buildPinLocationCache(
  schematic: SchematicRender
): PinLocationCache {
  const pinsByKey = new Map<string, Point>();
  const allPins: Point[] = [];

  // Instance pins
  for (const instance of schematic.instances) {
    for (const pinLoc of instance.getPinWorldLocations()) {
      const key = pointKey(pinLoc);
      if (!pinsByKey.has(key)) {
        pinsByKey.set(key, pinLoc);
        allPins.push(pinLoc);
      }
    }
  }

  // Schematic pins (external ports)
  for (const schPin of schematic.pins) {
    const pinLoc = getSchPinWorldLocation(schPin);
    const key = pointKey(pinLoc);
    if (!pinsByKey.has(key)) {
      pinsByKey.set(key, pinLoc);
      allPins.push(pinLoc);
    }
  }

  return { pinsByKey, allPins };
}

// Find the nearest pin to a given point.
// Returns {snapPoint: null, distance: null} if the cache is empty.
// Returns {snapPoint: null, distance: <dist>} if nearest pin is outside snap radius.
// Returns {snapPoint: <pin>, distance: <dist>} if a pin is within snap radius.
//
// TODO: For schematics with hundreds/thousands of pins, this O(n) linear search
// could cause lag during wire drawing. Consider spatial indexing (quadtree or
// grid-based bucketing) if performance becomes an issue.
export function findNearestPin(
  point: Point,
  cache: PinLocationCache
): PinSnapResult {
  // Early return for empty cache
  if (cache.allPins.length === 0) {
    return { snapPoint: null, distance: null };
  }

  let nearestPin: Point | null = null;
  let nearestDistance = Infinity;

  for (const pinLoc of cache.allPins) {
    const dx = point.x - pinLoc.x;
    const dy = point.y - pinLoc.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestPin = pinLoc;
    }
  }

  // Only snap if within the snap radius
  if (nearestPin && nearestDistance <= PIN_SNAP_RADIUS) {
    return {
      snapPoint: nearestPin,
      distance: nearestDistance,
    };
  }

  return {
    snapPoint: null,
    distance: nearestPin ? nearestDistance : null,
  };
}

// Check if a point is at a pin location (exact match)
export function isAtPin(point: Point, cache: PinLocationCache): boolean {
  return cache.pinsByKey.has(pointKey(point));
}

// Snap a point to a nearby pin if within radius, otherwise return null
export function snapToNearbyPin(
  point: Point,
  cache: PinLocationCache
): Point | null {
  const result = findNearestPin(point, cache);
  return result.snapPoint;
}
